import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

function ChecklistInvullen() {
  const { werkorderId } = useParams();
  const navigate = useNavigate();
  const { gebruiker } = useAuth();
  const canvasRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [werkorder, setWerkorder] = useState(null);
  const [template, setTemplate] = useState(null);
  const [secties, setSecties] = useState([]);
  const [items, setItems] = useState([]);
  const [invulling, setInvulling] = useState({});
  const [handtekeningen, setHandtekeningen] = useState({});
  const [showSignModal, setShowSignModal] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    loadChecklist();
  }, [werkorderId]);

  const loadChecklist = async () => {
    try {
      setLoading(true);
      const response = await api.get('/checklists/werkorder/' + werkorderId);
      setWerkorder(response.data.werkorder);
      setTemplate(response.data.template);
      setSecties(response.data.secties || []);
      setItems(response.data.items || []);
      const invullingMap = {};
      (response.data.invulling || []).forEach(inv => {
        invullingMap[inv.template_item_id] = inv;
      });
      setInvulling(invullingMap);
      const htMap = {};
      (response.data.handtekeningen || []).forEach(ht => {
        htMap[ht.type] = ht;
      });
      setHandtekeningen(htMap);
    } catch (error) {
      console.error('Fout bij laden checklist:', error);
      alert('Fout bij laden checklist');
    } finally {
      setLoading(false);
    }
  };

  const saveItem = async (itemId, data) => {
    try {
      await api.post('/checklists/werkorder/' + werkorderId + '/invullen', {
        template_item_id: itemId,
        ...data
      });
      setInvulling(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], ...data }
      }));
    } catch (error) {
      console.error('Fout bij opslaan:', error);
    }
  };

  const handleKeuzeChange = (itemId, keuze) => {
    saveItem(itemId, { waarde_keuze: keuze });
  };

  const handleTekstChange = (itemId, tekst) => {
    setInvulling(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], waarde_tekst: tekst }
    }));
  };

  const handleTekstBlur = (itemId) => {
    const value = invulling[itemId]?.waarde_tekst;
    if (value !== undefined) {
      saveItem(itemId, { waarde_tekst: value });
    }
  };

  const handleNummerChange = (itemId, nummer) => {
    setInvulling(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], waarde_nummer: nummer }
    }));
  };

  const handleNummerBlur = (itemId) => {
    const value = invulling[itemId]?.waarde_nummer;
    if (value !== undefined) {
      saveItem(itemId, { waarde_nummer: value });
    }
  };

  const openSignModal = () => {
    setShowSignModal(true);
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }, 100);
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    try {
      await api.post('/checklists/werkorder/' + werkorderId + '/handtekening', {
        type: 'monteur',
        naam: gebruiker?.voornaam + ' ' + gebruiker?.achternaam,
        handtekening_data: dataUrl
      });
      setHandtekeningen(prev => ({
        ...prev,
        monteur: { type: 'monteur', naam: gebruiker?.voornaam + ' ' + gebruiker?.achternaam, handtekening_data: dataUrl }
      }));
      setShowSignModal(false);
    } catch (error) {
      alert('Fout bij opslaan handtekening');
    }
  };

  const handleAfronden = async () => {
    if (!handtekeningen.monteur) {
      alert('Plaats eerst je handtekening');
      return;
    }
    try {
      setSaving(true);
      await api.post('/checklists/werkorder/' + werkorderId + '/afronden');
      alert('Checklist afgerond!');
      navigate('/werkorder/' + werkorderId);
    } catch (error) {
      alert('Fout bij afronden checklist');
    } finally {
      setSaving(false);
    }
  };

  const downloadPDF = () => {
    window.open('/api/checklists/werkorder/' + werkorderId + '/pdf', '_blank');
  };

  const getProgress = () => {
    const total = items.filter(i => i.type === 'goed_fout_nvt' || i.type === 'goed_fout').length;
    const filled = items.filter(i => invulling[i.id]?.waarde_keuze).length;
    return total > 0 ? Math.round((filled / total) * 100) : 0;
  };

  if (loading) {
    return <div style={{textAlign:'center',padding:'60px',color:'#6b7280'}}>Checklist laden...</div>;
  }

  if (!template) {
    return (
      <div style={{textAlign:'center',padding:'60px 20px'}}>
        <div style={{fontSize:'64px',marginBottom:'16px'}}>📋</div>
        <h2>Geen checklist gekoppeld</h2>
        <p style={{color:'#6b7280'}}>Er is nog geen checklist template aan deze werkorder gekoppeld.</p>
        <button onClick={() => navigate('/werkorder/' + werkorderId)} style={{marginTop:'20px',padding:'12px 24px',background:'#3b82f6',color:'white',border:'none',borderRadius:'8px',cursor:'pointer'}}>
          Terug naar werkorder
        </button>
      </div>
    );
  }

  const progress = getProgress();

  return (
    <div style={{minHeight:'100vh',background:'#f3f4f6'}}>
      <div style={{background:'linear-gradient(135deg, #1e3a5f, #2563eb)',color:'white',padding:'16px 20px',display:'flex',alignItems:'center',gap:'16px',position:'sticky',top:0,zIndex:100}}>
        <button onClick={() => navigate('/werkorder/' + werkorderId)} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'white',padding:'8px 16px',borderRadius:'6px',cursor:'pointer'}}>
          Terug
        </button>
        <div style={{flex:1}}>
          <h1 style={{margin:0,fontSize:'18px'}}>{template.naam}</h1>
          <p style={{margin:'4px 0 0 0',fontSize:'13px',opacity:0.8}}>{werkorder?.titel}</p>
        </div>
        <button onClick={downloadPDF} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'white',padding:'8px 16px',borderRadius:'6px',cursor:'pointer'}}>
          PDF
        </button>
      </div>

      <div style={{background:'white',padding:'16px 20px',display:'flex',alignItems:'center',gap:'12px',borderBottom:'1px solid #e5e7eb'}}>
        <div style={{flex:1,height:'8px',background:'#e5e7eb',borderRadius:'4px',overflow:'hidden'}}>
          <div style={{height:'100%',background:'linear-gradient(90deg, #10b981, #34d399)',width:progress+'%',transition:'width 0.3s'}}></div>
        </div>
        <span style={{fontSize:'14px',fontWeight:'600'}}>{progress}%</span>
      </div>

      <div style={{padding:'16px',paddingBottom:'100px'}}>
        {items.map(item => {
          const inv = invulling[item.id] || {};
          return (
            <div key={item.id} style={{background:'white',borderRadius:'8px',padding:'16px',marginBottom:'12px',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
              <div style={{marginBottom:'12px',fontWeight:'500'}}>{item.label}</div>
              {(item.type === 'goed_fout_nvt' || item.type === 'goed_fout') && (
                <div style={{display:'flex',gap:'8px'}}>
                  <button onClick={() => handleKeuzeChange(item.id, 'goed')} style={{flex:1,padding:'12px',border:'2px solid',borderColor:inv.waarde_keuze==='goed'?'#10b981':'#e5e7eb',background:inv.waarde_keuze==='goed'?'#10b981':'white',color:inv.waarde_keuze==='goed'?'white':'#059669',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>
                    Goed
                  </button>
                  <button onClick={() => handleKeuzeChange(item.id, 'fout')} style={{flex:1,padding:'12px',border:'2px solid',borderColor:inv.waarde_keuze==='fout'?'#ef4444':'#e5e7eb',background:inv.waarde_keuze==='fout'?'#ef4444':'white',color:inv.waarde_keuze==='fout'?'white':'#dc2626',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>
                    Fout
                  </button>
                  {item.type === 'goed_fout_nvt' && (
                    <button onClick={() => handleKeuzeChange(item.id, 'nvt')} style={{flex:1,padding:'12px',border:'2px solid',borderColor:inv.waarde_keuze==='nvt'?'#9ca3af':'#e5e7eb',background:inv.waarde_keuze==='nvt'?'#9ca3af':'white',color:inv.waarde_keuze==='nvt'?'white':'#6b7280',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>
                      N.v.t.
                    </button>
                  )}
                </div>
              )}
              {item.type === 'tekst' && (
                <input type="text" value={inv.waarde_tekst || ''} onChange={(e) => handleTekstChange(item.id, e.target.value)} onBlur={() => handleTekstBlur(item.id)} placeholder="Vul in..." style={{width:'100%',padding:'12px',border:'1px solid #d1d5db',borderRadius:'8px',boxSizing:'border-box'}} />
              )}
              {(item.type === 'nummer' || item.type === 'nummer_eenheid') && (
                <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  <input type="number" value={inv.waarde_nummer || ''} onChange={(e) => handleNummerChange(item.id, e.target.value)} onBlur={() => handleNummerBlur(item.id)} placeholder="0" style={{flex:1,padding:'12px',border:'1px solid #d1d5db',borderRadius:'8px'}} />
                  {item.eenheid && <span style={{color:'#6b7280',fontWeight:'500'}}>{item.eenheid}</span>}
                </div>
              )}
              {item.type === 'ip_adres' && (
                <input type="text" value={inv.waarde_tekst || ''} onChange={(e) => handleTekstChange(item.id, e.target.value)} onBlur={() => handleTekstBlur(item.id)} placeholder="192.168.1.1" style={{width:'100%',padding:'12px',border:'1px solid #d1d5db',borderRadius:'8px',boxSizing:'border-box'}} />
              )}
            </div>
          );
        })}

        <div style={{background:'white',borderRadius:'8px',padding:'20px',marginTop:'20px'}}>
          <h3 style={{margin:'0 0 16px 0'}}>Handtekening Monteur</h3>
          {handtekeningen.monteur ? (
            <div style={{textAlign:'center'}}>
              <img src={handtekeningen.monteur.handtekening_data} alt="Handtekening" style={{maxWidth:'250px',border:'1px solid #e5e7eb',borderRadius:'8px'}} />
              <p style={{margin:'8px 0',color:'#6b7280'}}>{handtekeningen.monteur.naam}</p>
              <button onClick={openSignModal} style={{background:'#f3f4f6',border:'1px solid #d1d5db',padding:'8px 16px',borderRadius:'6px',cursor:'pointer'}}>
                Opnieuw tekenen
              </button>
            </div>
          ) : (
            <button onClick={openSignModal} style={{width:'100%',background:'linear-gradient(135deg, #8b5cf6, #7c3aed)',color:'white',border:'none',padding:'16px',borderRadius:'10px',fontSize:'16px',cursor:'pointer'}}>
              Plaats Handtekening
            </button>
          )}
        </div>
      </div>

      <div style={{position:'fixed',bottom:0,left:0,right:0,background:'white',padding:'16px 20px',boxShadow:'0 -2px 10px rgba(0,0,0,0.1)'}}>
        <button onClick={handleAfronden} disabled={saving} style={{width:'100%',background:'linear-gradient(135deg, #10b981, #059669)',color:'white',border:'none',padding:'16px',borderRadius:'10px',fontSize:'16px',fontWeight:'600',cursor:'pointer',opacity:saving?0.6:1}}>
          {saving ? 'Opslaan...' : 'Checklist Afronden'}
        </button>
      </div>

      {showSignModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'20px'}}>
          <div style={{background:'white',borderRadius:'16px',width:'100%',maxWidth:'450px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',borderBottom:'1px solid #e5e7eb'}}>
              <h2 style={{margin:0,fontSize:'18px'}}>Handtekening</h2>
              <button onClick={() => setShowSignModal(false)} style={{background:'none',border:'none',fontSize:'24px',cursor:'pointer',color:'#9ca3af'}}>x</button>
            </div>
            <div style={{padding:'20px'}}>
              <p style={{margin:'0 0 16px 0',color:'#6b7280'}}>Teken je handtekening:</p>
              <canvas ref={canvasRef} width={400} height={200} style={{width:'100%',height:'200px',border:'2px solid #e5e7eb',borderRadius:'8px',touchAction:'none'}} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
            </div>
            <div style={{display:'flex',gap:'12px',padding:'16px 20px',borderTop:'1px solid #e5e7eb',background:'#f9fafb'}}>
              <button onClick={clearSignature} style={{flex:1,padding:'12px',background:'white',border:'1px solid #d1d5db',borderRadius:'8px',cursor:'pointer'}}>Wissen</button>
              <button onClick={saveSignature} style={{flex:1,padding:'12px',background:'#3b82f6',color:'white',border:'none',borderRadius:'8px',cursor:'pointer'}}>Opslaan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChecklistInvullen;
